import { supabase } from '../../../src/lib/supabase';
import { Rateio, RateioItem, RateioUnidadeGeradora } from '../../../types';
import { baixarPlanilhaRateio, gerarPlanilhaRateioBlob, nomeArquivoPlanilha } from './rateioExcelTemplate';

const BUCKET = 'rateio-planilhas';

// Mesmo padrão de src/services/descobrirCnpj.ts — a Edge Function responde
// { error: '<código>', message: '<texto>' } em falhas; prioriza `message`
// (texto humano) sobre `error` (código curto).
async function extractFunctionErrorMessage(res: { data?: any; error?: any }): Promise<string> {
  if (res.data?.message) return res.data.message;
  try {
    const body = await res.error?.context?.clone?.().json();
    if (body?.message) return body.message;
    if (typeof body?.error === 'string') return body.error;
  } catch {
    // corpo não é JSON ou já foi consumido — cai no fallback abaixo
  }
  return res.error?.message || 'Erro ao enviar a planilha de protocolo.';
}

export async function aprovarRateio(rateioId: string): Promise<void> {
  const { error } = await supabase.rpc('aprovar_rateio', { p_rateio_id: rateioId });
  if (error) throw error;
}

export async function marcarProtocoladoManual(rateioId: string): Promise<void> {
  const { error } = await supabase.rpc('marcar_rateio_protocolado', { p_rateio_id: rateioId, p_via: 'manual' });
  if (error) throw error;
}

export async function reportarFalha(rateioId: string, motivo: string, usuarioNome?: string | null): Promise<void> {
  const { error } = await supabase.rpc('reportar_falha_rateio', {
    p_rateio_id: rateioId,
    p_motivo: motivo,
    p_usuario_nome: usuarioNome || null,
  });
  if (error) throw error;
}

export async function marcarConciliado(rateioId: string): Promise<void> {
  const { error } = await supabase.rpc('marcar_rateio_conciliado', { p_rateio_id: rateioId });
  if (error) throw error;
}

/**
 * Gera a planilha, sobe pro Storage e chama a Edge Function `enviar-protocolo-rateio`,
 * que envia o e-mail direto via Resend (com o .xlsx anexado) pro e-mail da
 * distribuidora e/ou o e-mail principal configurado em Config > APIs — a API key
 * do Resend nunca chega ao navegador. Sem nenhum destinatário configurado (a
 * function responde `{ success: false, reason: 'no_recipient' }`), só baixa o
 * arquivo localmente — o usuário envia por fora e confirma com "Marcar como Protocolado".
 */
export async function enviarPlanilhaProtocolo({
  ug,
  rateio,
  itens,
  observacao,
  emailCopia,
}: {
  ug: RateioUnidadeGeradora;
  rateio: Rateio;
  itens: RateioItem[];
  observacao?: string;
  emailCopia?: string;
}): Promise<{ via: 'email' | 'manual'; enviado: boolean }> {
  const colunas = ug.distribuidora_config?.colunas_planilha;
  const blob = gerarPlanilhaRateioBlob(itens, colunas);
  const path = `${ug.id}/${rateio.id}.xlsx`;

  const { error: uploadError } = await supabase.storage.from(BUCKET).upload(path, blob, {
    contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    upsert: true,
  });
  if (uploadError) throw uploadError;

  const { data: signedUrlData, error: signedUrlError } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, 3600);
  if (signedUrlError) throw signedUrlError;

  const invokeRes = await supabase.functions.invoke('enviar-protocolo-rateio', {
    body: {
      rateio_id: rateio.id,
      ug_id: ug.id,
      ug_nome: ug.nome,
      mes_referencia: rateio.mes_referencia,
      nome_arquivo: nomeArquivoPlanilha(ug, rateio),
      planilha_url: signedUrlData.signedUrl,
      observacao: observacao || null,
      email_copia: emailCopia || null,
    },
  });

  if (invokeRes.error) throw new Error(await extractFunctionErrorMessage(invokeRes));

  if (!invokeRes.data?.success) {
    if (invokeRes.data?.reason === 'no_recipient') {
      baixarPlanilhaRateio(ug, rateio, itens, colunas);
      return { via: 'manual', enviado: false };
    }
    throw new Error(invokeRes.data?.message || 'Falha ao enviar a planilha.');
  }

  // Guarda o PATH do objeto (não a URL assinada, que expira) — uma nova signed URL
  // pode ser gerada sob demanda a partir dele quando precisar reexibir o arquivo.
  const { error: rpcError } = await supabase.rpc('marcar_rateio_protocolado', {
    p_rateio_id: rateio.id,
    p_via: 'email',
    p_planilha_url: `${BUCKET}/${path}`,
  });
  if (rpcError) throw rpcError;

  return { via: 'email', enviado: true };
}
